import { makeStyles } from '@material-ui/styles'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { getVmSnapshotColumns } from 'app/plugins/openstack/components/images/helpers'
import { imagesSelector } from 'app/plugins/openstack/components/images/selectors'
import { listSecurityGroups } from 'app/plugins/openstack/components/networks/security-groups/actions'
import { securityGroupsByIdSelector } from 'app/plugins/openstack/components/networks/security-groups/selectors'
import { getVolumeSnapshotColumns } from 'app/plugins/openstack/components/storage/snapshots/helper'
import { volumeSnapshotsByIdSelector } from 'app/plugins/openstack/components/storage/snapshots/selectors'
import { volumesByIdSelector } from 'app/plugins/openstack/components/storage/volumes/selectors'
import { virtualMachinesByIdSelector } from 'app/plugins/openstack/components/vms/selectors'
import { useAppSelector } from 'app/store'
import Progress from 'core/components/progress/Progress'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import Grid from 'core/elements/grid'
import Text from 'core/elements/Text'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useListAction from 'core/hooks/useListAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import { getResmgrRoleConfig, listResmgrHosts } from 'openstack/components/infrastructure/actions'
import { getPortsForVm, listNetworks } from 'openstack/components/networks/actions'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'
import { humanReadableSize } from 'openstack/helpers'
import { prop } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isNilOrEmpty } from 'utils/fp'
import { formatTableDate } from 'utils/misc'
import { IHostDetailsPageTabs } from '../../infrastructure/hosts/host-details/model'
import { resmgrHostsByIdSelector } from '../../infrastructure/selectors'
import { SharedWithTenants, SubnetsCellComponent } from '../../networks/NetworksListPage'
import { networksSelector } from '../../networks/selectors'
import { StatusCellComponent } from '../../storage/snapshots/VolumeSnapshotsListPage'
import { VolumeSourceCell } from '../../storage/volumes/volume-details/VolumeSourceCell'
import FailedMigrationReasonDialog from '../FailedMigrationReasonDialog'
import { formatTimestampToLocal, isVolumeBackedVm } from '../helpers'
import VmErrorModal from '../VmErrorModal'
import VmStateCellComponent from '../VmStateCellComponent'
import OverviewCard from './OverviewCard'
import TasksPage from './TasksPage'
import { INetworkSelector } from '../../networks/model'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '370px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
  flavorTooltip: {
    padding: 8,
  },
  tooltipIcon: {
    color: theme.components.input.label.hint,
    '&:hover': { color: theme.palette.grey[700] },
  },
  withTooltip: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: 'max-content max-content',
    alignItems: 'center',
  },
}))

const volumeColumns = ({ hostsById, isAdmin }) => {
  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (val, volume) => {
        return (
          <SimpleLink
            src={routeManager.routes.openstack.volumeDetails.path({
              id: volume?.id,
              tab: IVolumeDetailsPageTabs.Overview,
            })}
          >
            {volume?.name || volume?.id}
          </SimpleLink>
        )
      },
    },
    {
      key: 'id',
      label: 'UUID',
      display: false,
    },
    {
      key: 'hostName',
      label: 'Host',
      render: (hostName) =>
        hostsById?.[hostName]?.name || hostName ? (
          <SimpleLink
            src={routeManager.routes.openstack.hostDetails.path({
              id: hostName,
              tab: IHostDetailsPageTabs.Overview,
            })}
          >
            {hostsById?.[hostName]?.name || hostName}
          </SimpleLink>
        ) : null,
      display: isAdmin(),
    },
    {
      key: 'status',
      label: 'Status',
      CellComponent: (props) => <StatusCellComponent {...props} />,
      display: false,
    },
    {
      key: 'volume_type',
      label: 'Type',
      display: false,
    },
    { key: 'description', label: 'Description' },
    { key: 'attachments', label: 'Device', render: (val) => val?.[0]?.device },
    {
      key: 'snapshots',
      label: 'Snapshots',
      render: (snapshots) => snapshots?.length,
    },
    { key: 'size', label: 'Size', render: (val) => humanReadableSize(val * 1024 * 1024 * 1024) },
    {
      key: 'bootable',
      label: 'Bootable',
      display: false,
    },
    {
      key: 'multiattach',
      label: 'Multiattach',
      render: (val) => (val ? 'True' : 'False'),
      display: false,
    },
    {
      key: 'source',
      label: (
        <TitleWithInfoIcon
          titleText="Source"
          tooltipMsg={'The resource used to create this volume. Blank if no resource used'}
        />
      ),
      CellComponent: VolumeSourceCell,
      display: false,
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (time: string) => formatTimestampToLocal(time),
      display: false,
    },
    {
      key: 'metadata',
      label: 'Metadata',
      CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
      display: false,
    },
  ]
  return columns
}

export default function Overview({ vm, loading, tasks, loadingTasks, volumes, loadingVolumes }) {
  const classes = useStyles({})
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const [pathOnHost, setPathOnHost] = useState('')
  const [vmPorts, setVmPorts] = useState([])
  const [loadingVmPorts, setLoadingVmPorts] = useState(true)
  const [errorVm, setErrorVm] = useState<string>(null)
  const [migrationFailureVm, setMigrationFailureVm] = useState(null)

  const isVolumeBackedInstance = useMemo(() => {
    return isVolumeBackedVm(vm)
  }, [vm])

  // Handle edge case: On browser refresh, Security Groups data is not populated.
  // Call listSecurityGroups action to repopulate the Security Groups under the Networks table.
  useListAction(listSecurityGroups)
  // Networks
  useListAction(listNetworks)
  useListAction(listResmgrHosts)
  const hostsById = useSelector(resmgrHostsByIdSelector)
  const allNetworks = useAppSelector(networksSelector)
  const volumesById = useSelector(volumesByIdSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)
  const vmsById = useSelector(virtualMachinesByIdSelector)
  const images = useSelector(imagesSelector)
  const securityGroupsById = useSelector(securityGroupsByIdSelector)

  const volumeTableColumns = useMemo(() => volumeColumns({ hostsById, isAdmin }), [hostsById])

  useEffect(() => {
    const getHypervisorInfo = async () => {
      const response: any = await getResmgrRoleConfig({
        role: isVmware ? 'pf9-ostackhost-neutron-vmw' : 'pf9-ostackhost-neutron',
        host: { id: vm?.['OS-EXT-SRV-ATTR:host'] },
      })
      setPathOnHost(response?.instances_path)
    }
    if (isAdmin() && !pathOnHost && vm?.['OS-EXT-SRV-ATTR:host']) {
      getHypervisorInfo()
    }
  }, [vm?.['OS-EXT-SRV-ATTR:host'], isVmware, pathOnHost])

  useEffect(() => {
    if (!vm?.id) {
      return
    }
    const getPorts = async () => {
      try {
        const response = await getPortsForVm({ vmId: vm.id })
        setVmPorts(response)
      } catch (e) {
        console.log(e)
      } finally {
        setLoadingVmPorts(false)
      }
    }
    getPorts()
  }, [vm?.id, vm?.security_groups])

  const addressTableColumns = useMemo(
    () => [
      {
        key: 'networkName',
        label: 'Network',
      },
      {
        key: 'id',
        label: 'UUID',
        display: false,
      },
      { key: 'addr', label: 'IP Address' },
      { key: 'OS-EXT-IPS-MAC:mac_addr', label: 'MAC Address' },
      { key: 'version', label: 'Version' },
      {
        key: 'OS-EXT-IPS:type',
        label: 'Type',
      },
      {
        key: 'index',
        label: 'Security Groups',
        render: (val, item) => {
          // not sure if there could be duplicate mac addresses or not
          const port = vmPorts.find(
            (vmPort) => item['OS-EXT-IPS-MAC:mac_addr'] === vmPort.mac_address,
          )
          const securityGroupsString = port?.security_groups
            ?.map((sgId) => securityGroupsById?.[sgId]?.name)
            ?.filter(Boolean)
            ?.join(', ')

          return securityGroupsString || 'N/A'
        },
      },
      {
        key: 'subnetDetails',
        label: 'Subnets',
        render: (value) => <SubnetsCellComponent value={value} />,
        display: false,
      },
      {
        key: 'port_security_enabled',
        label: 'Port Security',
        render: (val) => (val ? 'Enabled' : 'Disabled'),
        display: false,
      },
      {
        key: 'provider:network_type',
        label: 'Network Type',
        display: false,
      },
      {
        key: 'mtu',
        label: 'MTU',
        display: false,
      },
      {
        key: 'provider:physical_network',
        label: 'Physical Network',
        display: false,
      },
      {
        key: 'router:external',
        label: 'External',
        render: (val) => (val ? 'Yes' : 'No'),
        display: false,
      },
      {
        key: 'provider:segmentation_id',
        label: 'Segmentation ID',
        render: (val) => (val ? val : 'N/A'),
        display: false,
      },
      {
        key: 'admin_state_up',
        label: 'Admin State',
        render: (val) => (val ? 'Up' : 'Down'),
        display: false,
      },
      {
        key: 'dns_domain',
        label: 'DNS Zone',
        render: (val) => (val ? val : '-'),
        display: false,
      },
      {
        key: 'status',
        label: 'Status',
        display: false,
      },
      {
        key: 'availableIps',
        label: 'Available IPs',
        display: false,
      },
      {
        key: 'shared',
        label: 'Shared Tenants',
        render: (value, item) => <SharedWithTenants item={item} value={value} />,
        display: false,
      },
      {
        key: 'created_at',
        label: 'Created',
        render: (time: string) => formatTimestampToLocal(time),
        display: false,
      },
    ],
    [vmPorts, securityGroupsById],
  )

  const addresses = useMemo(() => {
    if (!vm?.addresses) {
      return []
    }

    const networkNames = Object.keys(vm?.addresses || {})
    return networkNames?.reduce((accum, name) => {
      const networkAddresses = vm?.addresses[name]
      // Matching network object for this network name
      const matchingNetwork =
        allNetworks?.find((net) => net.name === name) || ({} as INetworkSelector)
      const withNetworkName = !matchingNetwork?.isSimpleNetwork
        ? networkAddresses?.map((address, idx) => {
            return {
              ...address,
              ...matchingNetwork,
              networkName: name,
              index: idx,
            }
          })
        : [
            {
              ...matchingNetwork,
              networkName: name,
              // For Simple Network, addr and mac_addr are the available on VM metadata
              addr: vm?.metadata?.primary_ip,
              'OS-EXT-IPS-MAC:mac_addr': vm?.metadata?.mac_address,
            },
          ]
      return [...accum, ...withNetworkName]
    }, [])
  }, [vm])

  const attachedVolumeSnapshots = useMemo(() => {
    if (!vm?.volumeIds) {
      return []
    }
    return vm?.volumeIds?.reduce((accum, volumeId) => {
      const volume = volumesById[volumeId]
      if (volume?.snapshots) {
        return [...accum, ...volume.snapshots]
      }
      return accum
    }, [])
  }, [vm, volumesById])

  const attachedVmSnapshots = useMemo(() => {
    // Get all vm snapshopts from images list
    const vmsnapshots = images?.filter((image) =>
      ['ephemeralDiskSnapshot', 'vmVolumeSnapshot'].includes(image?.imageType),
    )

    return vmsnapshots?.filter((snapshot) => snapshot?.instance_uuid === vm?.id) || []
  }, [vm])

  const overviewData = useMemo(() => {
    if (!vm) {
      return []
    }
    return [
      {
        key: 'ID',
        value: vm?.id,
      },
      {
        key: 'Host',
        value: vm?.['OS-EXT-SRV-ATTR:hypervisor_hostname'],
      },
      !isVolumeBackedInstance && {
        key: 'Host Disk Location',
        value: pathOnHost,
      },
      {
        key: 'Name on Host',
        value: vm?.['OS-EXT-SRV-ATTR:instance_name'],
      },
      {
        key: 'VM State',
        value: vm?.state,
        render: (item) => (
          <VmStateCellComponent
            item={item}
            state={vm?.state}
            setErrorVm={setErrorVm}
            setMigrationFailureVm={setMigrationFailureVm}
          />
        ),
      },
      {
        key: 'Lease Expiration',
        value: vm?.expirationTime,
        render: (value) => formatTableDate(value),
      },
      {
        key: 'Image',
        value: vm?.imageName,
      },
      {
        key: 'Security Groups',
        value: vm?.security_groups?.map((group) => group?.name).join(', '),
      },
      {
        key: 'Volume Deletion on VM Termination',
        value: !!vm?.['os-extended-volumes:volumes_attached']?.[0]?.delete_on_termination
          ? 'Enabled'
          : 'Disabled',
        condition: (item) => {
          return item?.['os-extended-volumes:volumes_attached']?.length
        },
      },
      {
        key: 'Metadata',
        value: vm?.metadata,
        render: (item) => {
          return !isNilOrEmpty(vm?.metadata) ? (
            <Labels labels={vm?.metadata} variant="default" showMoreButton ellipsisAt={75} />
          ) : (
            <Text variant="caption1">No Metadata</Text>
          )
        },
      },
      {
        key: 'Specs',
        value: vm?.flavor,
        render: (item) => {
          const flavor = item?.flavor
          const hotplugMemory = Number(item?.metadata?.['HOTPLUG_MEMORY'])
          const hotplugVcpus = Number(item?.metadata?.['HOTPLUG_CPU'])
          return (
            <div className={classes.withTooltip}>
              <Text variant="caption1">{flavor?.original_name}</Text>
              <HintTooltip
                customBody={
                  <div className={classes.flavorTooltip}>
                    <div>
                      RAM:{' '}
                      <b>
                        {hotplugMemory
                          ? `${humanReadableSize(hotplugMemory * 1024 * 1024)} (hotplugged)`
                          : humanReadableSize(flavor?.ram * 1024 * 1024)}
                      </b>
                    </div>
                    <div>
                      vCPUs: <b>{hotplugVcpus ? `${hotplugVcpus} (hotplugged)` : flavor?.vcpus}</b>
                    </div>
                    <div>
                      Disk: <b>{humanReadableSize(flavor?.disk * 1024 * 1024 * 1024)}</b>
                    </div>
                  </div>
                }
              />
            </div>
          )
        },
      },
      {
        key: 'SSH Key',
        value: vm?.key_name || 'N/A',
      },
    ]
  }, [vm, pathOnHost])

  const volumeSnapshotTableColumns = useMemo(() => getVolumeSnapshotColumns({ volumesById }), [
    volumesById,
  ])

  const vmSnapshotColumns = useMemo(() => {
    return getVmSnapshotColumns({ vmsById, volumeSnapshotsById, hideKeys: ['id', 'instance_uuid'] })
  }, [vmsById, volumeSnapshotsById])

  return (
    <Progress loading={loading}>
      {errorVm && (
        <VmErrorModal
          hostName={vm?.['OS-EXT-SRV-ATTR:hypervisor_hostname']}
          vmId={errorVm}
          onClose={() => setErrorVm(null)}
        />
      )}
      {migrationFailureVm && (
        <FailedMigrationReasonDialog
          vm={migrationFailureVm}
          onClose={() => setMigrationFailureVm(null)}
        />
      )}
      <div className={classes.overview}>
        <div className={classes.column}>
          <OverviewCard item={vm} data={overviewData} icon="desktop" />
        </div>
        <div className={classes.column}>
          <TasksPage vm={vm} loading={loading} tasks={tasks} loadingTasks={loadingTasks} />
          <Grid
            uniqueIdentifier="addr"
            data={addresses || []}
            columns={addressTableColumns}
            loading={loading}
            compact
            label="Networks"
            showItemsCountInLabel={false}
          />
          <Grid
            uniqueIdentifier="id"
            data={volumes}
            columns={volumeTableColumns}
            loading={loadingVolumes}
            compact
            label="Volumes"
            showItemsCountInLabel={false}
          />
          {attachedVolumeSnapshots?.length ? (
            <Grid
              uniqueIdentifier="id"
              data={attachedVolumeSnapshots || []}
              columns={volumeSnapshotTableColumns}
              loading={loading}
              compact
              label="Volume Snapshots"
              showItemsCountInLabel={false}
            />
          ) : null}
          {attachedVmSnapshots?.length ? (
            <Grid
              uniqueIdentifier="id"
              data={attachedVmSnapshots || []}
              columns={vmSnapshotColumns}
              loading={loading}
              compact
              label="VM Snapshots"
              showItemsCountInLabel={false}
            />
          ) : null}
        </div>
      </div>
    </Progress>
  )
}
